import React from 'react';
import { formatMoney } from '../../../../utils/constants/costs';

export type ClientCostsSummaryRow = {
    id: string;
    clientName?: string;
    companyName?: string;
    username?: string;
    email?: string;
    totalUsd: number;
    calls: number;
    requests: number;
    inputTokens: number;
    outputTokens: number;
    clientId?: string;
    providers?: any;
};

const ClientCostsTable = ({
    rows,
    loading,
    hasMore,
    loadingMore,
    onLoadMore,
    scrollContainerRef,
    onViewDetails,
}: {
    rows?: ClientCostsSummaryRow[];
    loading?: boolean;
    hasMore?: boolean;
    loadingMore?: boolean;
    onLoadMore?: () => void;
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
    onViewDetails?: (clientId: string) => void;
}) => {
    const handleScroll = () => {
        if (!scrollContainerRef?.current || !hasMore || loadingMore || loading) return;
        const el = scrollContainerRef.current;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
            onLoadMore?.();
        }
    };

    return (
        <div>
            <div className='overflow-x-auto border border-1 rounded-xl dark:border-borderColor-darkMode'>
                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className='overflow-y-auto'
                    style={{ maxHeight: '600px' }}
                >
                    <table className='min-w-full bg-white dark:bg-black dark:text-white min-h-[220px]'>
                        <thead className='sticky top-0 z-10 bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-white'>
                            <tr>
                                <th className='px-4 py-3 min-w-[220px]'>Client</th>
                                <th className='px-4 py-3 min-w-[160px]'>Total (USD)</th>
                                <th className='px-4 py-3 min-w-[120px]'>Calls</th>
                                <th className='px-4 py-3 min-w-[140px]'>Input tokens</th>
                                <th className='px-4 py-3 min-w-[140px]'>Output tokens</th>
                                <th className='px-4 py-3 min-w-[220px]'>AI Models</th>
                                <th className='px-4 py-3 min-w-[140px]'>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading && (!rows || rows.length === 0) &&
                                Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={`s-${i}`} className='animate-pulse'>
                                        {Array.from({ length: 7 }).map((__, j) => (
                                            <td key={`c-${i}-${j}`} className='px-4 py-4'>
                                                <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4' />
                                            </td>
                                        ))}
                                    </tr>
                                ))}

                            {!loading && (!rows || rows.length === 0) && (
                                <tr>
                                    <td colSpan={7} className='px-4 py-8 text-center text-gray-500 dark:text-gray-400'>
                                        No data currently available
                                    </td>
                                </tr>
                            )}

                            {rows && rows.length > 0 && rows.map((r) => (
                                <tr key={r.id} className='hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm'>
                                    <td className='px-4 py-4'>
                                        <div className='flex flex-col'>
                                            <span className='font-medium'>{r.companyName || r.clientName || '--'}</span>
                                            <span className='text-xs text-gray-500 dark:text-gray-400'>
                                                {r.username ? `@${r.username}` : r.email || ''}
                                            </span>
                                        </div>
                                    </td>

                                    <td className='px-4 py-4 font-medium'>
                                        $ {formatMoney(r.totalUsd)}
                                    </td>

                                    <td className='px-4 py-4'>{r.calls ?? 0}</td>
                                    <td className='px-4 py-4'>{r.inputTokens ?? 0}</td>
                                    <td className='px-4 py-4'>{r.outputTokens ?? 0}</td>

                                    <td className='px-4 py-4 font-bold'>
                                        <span>
                                            {r.providers ? `${r.providers} ${r.providers > 1 ? 'AI Models' : 'AI Model'}` : '--'}
                                        </span>
                                    </td>

                                    <td className='px-4 py-4'>
                                        <button
                                            type='button'
                                            onClick={() => onViewDetails?.(r.clientId || '')}
                                            className='px-3 py-1 rounded border border-gray-300 dark:border-borderColor-darkMode hover:bg-gray-100 dark:hover:bg-black'
                                        >
                                            Details
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {loadingMore && rows && rows.length > 0 && (
                                <tr>
                                    <td colSpan={7} className='px-4 py-5'>
                                        <div className='flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400'>
                                            <svg className='h-5 w-5 animate-spin text-gray-400' viewBox='0 0 24 24' fill='none'>
                                                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4A4 4 0 008 12H4z' />
                                            </svg>
                                            <span>Loading more...</span>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {!loading && !loadingMore && rows && rows.length > 0 && hasMore === false && (
                                <tr>
                                    <td colSpan={7} className='px-4 py-4 text-center text-xs text-gray-400'>
                                        All clients loaded
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ClientCostsTable;